/**
 * Product capabilities — determines feature availability per device type.
 *
 * The Nautic Alert product line includes:
 *   - X3 / Insight (deviceType 1) — full-featured, supports Onboard mode, no built-in xPulse
 *   - VTracker (deviceType 2) — limited, no Onboard mode, built-in xPulse zones 1-2
 *   - Triton (deviceType 3) — limited, no Onboard mode, built-in xPulse zones 3-4 auto-configured as Electronics + Engine Cowling
 *
 * Device type comes from deviceProperties.deviceType as a numeric value.
 *
 * Built-in xPulse zones are part of the main unit (not wireless).
 * Their sourceAddress matches the first entry in sensorMapList with sensorType=2 (LOOKOUT_SENSOR_TYPE).
 * These zones should NOT show "Disconnected" since they are hardwired.
 *
 * System zones (tamper=58, inactivity=59, low battery=60) are internal to the xPulse module.
 * For built-in xPulse, these are hidden. For external wireless xPulse Plus, they may be shown.
 */

// ── Device type constants (from C# NADeviceType enum) ──

export const DEVICE_TYPE = {
  UNKNOWN: 0,
  INSIGHT: 1,    // X3 / X3 Pro
  VTRACKER: 2,
  TRITON: 3,
} as const;

export type DeviceTypeNum = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE];

// ── Product capabilities interface ──

export interface ProductCapabilities {
  deviceType: DeviceTypeNum;
  deviceTypeName: string;

  /** Supports Onboard security mode (in addition to Disarmed/Away) */
  supportsOnboardMode: boolean;

  /** Maximum number of user-configurable security zones shown in the UI */
  maxVisibleZoneInstances: number;

  /** Zone instances (0-based) that are built into the device (not wireless) */
  builtInZoneInstances: number[];

  /**
   * For Triton: zones 3-4 are auto-configured with specific device categories
   * (Electronics=8, Engine Cowling=14) when not overridden by the user.
   * These zones are shown in the security panel using their deviceCategory label,
   * not as generic "Security" zones.
   */
  autoConfiguredZones: { zoneNum: number; defaultCategory: number; label: string }[];

  /**
   * When zone 0 (zoneInstance 0) is configured as arm/disarm control (alarmType 32),
   * the switch changes to "reserved" mode: the switch output drives an armed-state LED
   * and a key switch input is used for arm/disarm. In this state the switch should not
   * be shown as a user-controllable float/bilge switch.
   */
  switchReservedOnArmDisarmZone: boolean;

  /** Show the onboard siren enable/disable setting in System Settings (Insight/X3 only) */
  showSirenSetting: boolean;

  /** Device is equipped with an Iridium satellite modem */
  supportsSatellite: boolean;

  /** Geofence siren setting exposed in configuration */
  showGeofenceSirenSetting: boolean;

  /** Full geofence auto-arm options (including Onboard) */
  fullGeofenceAutoArmOptions: boolean;

  /** Show system zones (tamper, inactivity, low batt) for built-in xPulse */
  showBuiltInSystemZones: boolean;

  /**
   * Mobile's whole "Nautic Alert Wireless Expansion" section — pairing
   * EXTERNAL wireless xPulse sensors, the paired-sensor list, AND the
   * device's own built-in zone config drill-down — is gated behind a single
   * `DeviceType != DEVICE_TRITON → return` in `DeviceConfigPageViewModel.cs`
   * (`initView()` line 392-393 gates the section's construction;
   * `RefreshModel()` line 472-473 gates both `refreshSensorManagement()` and
   * `refreshDeviceZones()` together). Unconditional on DeviceType alone, no
   * hardwareRev/protocolRev qualifier — Insight and VTracker never construct
   * any of it. Gates both the Sensors page and `DeviceZoneSettings` (on the
   * Configuration page's System Settings tab). Distinct from `builtInZoneInstances` above,
   * which is about which zone slots are hardwired vs. externally-paired,
   * not whether this device type shows the section at all.
   */
  supportsWirelessExpansion: boolean;
}

// ── Resolve capabilities from device type ──

export function getProductCapabilities(deviceType: unknown): ProductCapabilities {
  const dt = typeof deviceType === 'number' ? deviceType : DEVICE_TYPE.UNKNOWN;

  switch (dt) {
    case DEVICE_TYPE.INSIGHT:
      return {
        deviceType: DEVICE_TYPE.INSIGHT,
        deviceTypeName: 'X3',
        supportsOnboardMode: true,
        maxVisibleZoneInstances: 4,
        builtInZoneInstances: [], // X3 has no built-in xPulse
        autoConfiguredZones: [],
        switchReservedOnArmDisarmZone: false,
        showSirenSetting: true,
        showGeofenceSirenSetting: true,
        fullGeofenceAutoArmOptions: true,
        showBuiltInSystemZones: false,
        supportsSatellite: true,
        supportsWirelessExpansion: false,
      };

    case DEVICE_TYPE.VTRACKER:
      return {
        deviceType: DEVICE_TYPE.VTRACKER,
        deviceTypeName: 'VTracker',
        supportsOnboardMode: false,
        maxVisibleZoneInstances: 4,
        builtInZoneInstances: [0, 1], // Zones 1-2 (zoneInstance 0-1)
        autoConfiguredZones: [],
        switchReservedOnArmDisarmZone: true,
        showSirenSetting: false,
        showGeofenceSirenSetting: false,
        fullGeofenceAutoArmOptions: false,
        showBuiltInSystemZones: false,
        supportsSatellite: true,
        supportsWirelessExpansion: false,
      };

    case DEVICE_TYPE.TRITON:
      return {
        deviceType: DEVICE_TYPE.TRITON,
        deviceTypeName: 'Triton',
        supportsOnboardMode: false,
        maxVisibleZoneInstances: 4,
        builtInZoneInstances: [0, 1, 2, 3], // All 4 zones are built-in
        autoConfiguredZones: [
          { zoneNum: 3, defaultCategory: 8, label: 'Electronics' },   // DEVICE_CATEGORY_ELECTRONICS
          { zoneNum: 4, defaultCategory: 14, label: 'Engine Cowling' }, // DEVICE_CATEGORY_ENGINE_COWLING
        ],
        switchReservedOnArmDisarmZone: true,
        showSirenSetting: false,
        showGeofenceSirenSetting: false,
        fullGeofenceAutoArmOptions: false,
        showBuiltInSystemZones: false,
        supportsSatellite: true,
        supportsWirelessExpansion: true,
      };

    default:
      return {
        deviceType: DEVICE_TYPE.UNKNOWN,
        deviceTypeName: 'Unknown',
        supportsOnboardMode: true, // default to full features
        maxVisibleZoneInstances: 4,
        builtInZoneInstances: [],
        autoConfiguredZones: [],
        switchReservedOnArmDisarmZone: false,
        showSirenSetting: true,
        showGeofenceSirenSetting: true,
        fullGeofenceAutoArmOptions: true,
        showBuiltInSystemZones: false,
        supportsSatellite: true,
        // Mobile's gate is a strict `DeviceType == DEVICE_TRITON` check, not
        // "permissive unless known-unsupported" — unlike the other flags in
        // this branch, an unrecognized device type does NOT default to
        // enabled here.
        supportsWirelessExpansion: false,
      };
  }
}

// ── Helper: is this zone instance built into the device? ──

export function isBuiltInZone(
  capabilities: ProductCapabilities,
  zone: { zoneInstance?: number; sourceAddressMSB?: number; sourceAddressLSB?: number },
): boolean {
  // If zone instance is in the built-in list
  if (zone.zoneInstance !== undefined && capabilities.builtInZoneInstances.includes(zone.zoneInstance)) {
    return true;
  }
  return false;
}

// ── Helper: get the display label for a zone, respecting auto-configuration ──

export function getAutoConfigLabel(
  capabilities: ProductCapabilities,
  zone: { zoneNum?: number; deviceCategory?: unknown },
): string | null {
  // Check if this zone has an auto-configured category
  const autoConfig = capabilities.autoConfiguredZones.find((ac) => ac.zoneNum === zone.zoneNum);
  if (autoConfig) {
    // If deviceCategory matches the auto-config default, use the auto-config label
    if (zone.deviceCategory === autoConfig.defaultCategory || zone.deviceCategory === 0) {
      return autoConfig.label;
    }
  }
  return null;
}

// ── Helper: is the switch in reserved mode (arm/disarm LED output + key switch input)? ──

export function isSwitchReserved(
  capabilities: ProductCapabilities,
  securityZones: { alarmType?: unknown; zoneInstance?: number }[],
): boolean {
  if (!capabilities.switchReservedOnArmDisarmZone) return false;

  // When zone 0 (zoneInstance 0) is configured as arm/disarm control (alarmType 32),
  // the switch output drives an armed-state LED and the key switch input is used for
  // arm/disarm. The switch should not be shown as a float/bilge sensor.
  return securityZones.some(
    (z) => z.zoneInstance === 0 && (z.alarmType === 32 || z.alarmType === 'ALARM_TYPE_ZONE_GEOFENCE_ARM_DISARM_VTRACKER'),
  );
}

// ── Helper: should system zones be shown for this zone? ──

/**
 * System zones (tamper, inactivity, low battery) are hidden for
 * the built-in xPulse module (sourceAddress matching the device's
 * primary sensor at MSB=0/1, LSB=1). They are only visible for
 * external wireless xPulse Plus sensors that have unique addresses.
 */
export function shouldShowSystemZone(
  _capabilities: ProductCapabilities,
  zone: { zoneInstance?: number; sourceAddressMSB?: number; sourceAddressLSB?: number },
): boolean {
  // Built-in xPulse (sourceAddress MSB<=1, LSB<=1) — always hide system zones
  const msb = zone.sourceAddressMSB ?? 0;
  const lsb = zone.sourceAddressLSB ?? 0;
  if (msb <= 1 && lsb <= 1) {
    return false;
  }
  // External wireless xPulse Plus — show system zones
  return true;
}

// ── Helper: is this sensor on the internal XPulse bus? ──

/**
 * Returns true for sensors wired directly to the device's internal XPulse bus
 * (sourceAddressMSB ≤ 1, sourceAddressLSB ≤ 1). These integrated sensors do not
 * expose a meaningful network online/offline state and should not display
 * connectivity badges on any application tile.
 */
export function isIntegratedXPulseSensor(
  sensor: { sourceAddressMSB: number; sourceAddressLSB: number },
): boolean {
  return sensor.sourceAddressMSB <= 1 && sensor.sourceAddressLSB <= 1;
}
